// Wake-on-LAN readiness — whether a station can be woken from off by a magic packet (F-STN-17; spec/10 §6.5,
// spec/13 §3).
//
//   PUT /v1/station/wake-readiness                                              station-authenticated
//   GET /v1/organizations/{o}/venues/{v}/stations/{stationId}/wake-readiness    stations:read
//   GET /v1/station/wake-list                                                   station-authenticated
//   POST /v1/station/wake-sends                                                 station-authenticated
//
// **Waking without the cloud (2026-10-01).** A magic packet is a LAN broadcast: some device on the LAN that is ON
// has to send it. The console's Wake relays through an online station (`station.wol.send`), which needs the cloud
// and one station up. So every station — and the counter PC running Clavian Counter — also keeps the venue's
// **wake list** (each device's number, MACs and subnet broadcast addresses, the opening hours and
// `venue.powerSchedule`) in its data folder, refreshed whenever it is online, and can wake the venue's PCs itself
// with no server: Clavian Counter's Wake all / wake one / the scheduled opening-hours wake, and the kiosk staff
// menu's Wake all. What a device sent is reported back (`wake-sends`) when it is next online, for the audit log.
//
// The BIOS has to allow Wake-on-LAN, but Windows decides the rest: Fast Startup (a hybrid shutdown that leaves
// the NIC unarmed, so a PC that was "shut down" is really hibernated and ignores the packet), and each wired
// adapter's "Wake on Magic Packet", "Allow this device to wake the computer" and its power management (which has to
// be ON: "Allow the computer to turn off this device to save power" unticked greys out the wake options, because an
// adapter Windows may not power-manage cannot be armed). The station service (elevated) sets those once paired, records
// what it changed in its revert journal (so an uninstall puts them back), and reports what it found here — at
// startup, after pairing and whenever the result changes; never as a heartbeat.
//
// The server keeps the latest report per station. The console's station drawer reads it ("Wake-on-LAN: ready" or
// "needs attention" with the reason), and the wake action uses the MAC addresses of the adapters the station
// armed, ahead of the ones its pairing recorded — so a PC whose adapter changed since pairing still wakes.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { HolidaysSchema, OpeningHoursSchema } from "../primitives/opening-hours.js";
import { TimezoneSchema } from "../primitives/scalars.js";
import { StationModeSchema } from "../realtime/station.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { STATIONS_READ } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { StationRoleSchema } from "./stations.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5); a user session, a member or ' +
    'a player token, an expired token and a revoked device are each refused with `401`.';
/** A MAC address as the station writes it: six upper-case hex bytes, colon-separated (as `HardwareSummary`). */
export const WakeMacAddressSchema = z
    .string()
    .regex(/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/)
    .openapi({ description: 'A MAC address, upper-case and colon-separated.', example: '00:1A:2B:3C:4D:5E' });
/** A dotted-quad IPv4 address. */
export const Ipv4AddressSchema = z
    .string()
    .regex(/^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/)
    .openapi({ description: 'An IPv4 address, dotted-quad.', example: '192.168.1.20' });
/** At most this many adapters in one report — more wired NICs than any PC has. */
export const MAX_WAKE_ADAPTERS = 16;
/**
 * Why a station is not ready to wake (F-STN-17): each is one thing Windows or the hardware still stands in the way
 * of, in the order staff should look at them.
 *
 *   fast_startup_on         Fast Startup is on: "shut down" hibernates, and most NICs will not wake from it.
 *   no_wired_adapter        No physical wired adapter: Wi-Fi and virtual adapters cannot be woken by a packet.
 *   magic_packet_off        No wired adapter has "Wake on Magic Packet" on (the driver may not offer it).
 *   device_wake_off         Windows does not let any wired adapter wake the computer.
 *   power_management_off    Windows power management is off for the adapter (`PnPCapabilities` 0x08/0x10), so it
 *                           cannot be armed to wake — the common "stop Windows turning the NIC off" tweak does this.
 *   apply_failed            The station could not change a setting (see the station's log).
 */
export const WAKE_ISSUES = ['fast_startup_on', 'no_wired_adapter', 'magic_packet_off', 'device_wake_off', 'power_management_off', 'apply_failed'];
export const WakeIssueSchema = z.enum(WAKE_ISSUES).openapi('WakeIssue', {
    description: 'Why a station is not ready to be woken by Wake-on-LAN: Fast Startup on, no wired adapter, Wake on Magic Packet off, ' +
        'the adapter not allowed to wake the computer, the adapter’s power management off, or a setting the station could not apply.',
});
/** One physical wired adapter, as the station found it after applying its settings. */
export const WakeAdapterSchema = z
    .object({
    mac: WakeMacAddressSchema,
    /** The adapter's name as Windows shows it ("Intel(R) Ethernet Connection I219-V"). */
    name: z.string().trim().min(1).max(256),
    /** "Wake on Magic Packet" is on (`*WakeOnMagicPacket` = 1, or the driver's own equivalent). */
    magicPacket: z.boolean(),
    /** Windows allows the adapter to wake the computer (`MSPower_DeviceWakeEnable`, `powercfg /devicequery wake_armed`). */
    wakeArmed: z.boolean(),
    /** Windows power management is on for the adapter — `PnPCapabilities` has neither 0x08 nor 0x10 — so it can be armed. */
    powerManagementOn: z.boolean(),
    /** Every one of the three above: this adapter will wake the PC from off, the BIOS permitting. */
    ready: z.boolean(),
    /**
     * The adapter's IPv4 address when it had one (2026-10-01, additive) — with `ipv4PrefixLength`, the subnet whose
     * directed broadcast (`192.168.1.255` for `192.168.1.20/24`) a wake sends to, for a LAN where the limited
     * broadcast does not reach every segment.
     */
    ipv4Address: Ipv4AddressSchema.optional(),
    /** The subnet's prefix length (24 for 255.255.255.0), with `ipv4Address`. */
    ipv4PrefixLength: z.int().min(8).max(30).optional(),
})
    .openapi('WakeAdapter', { description: 'A wired network adapter of a station and whether it is armed for Wake-on-LAN (F-STN-17).' });
/** The report's fields, shared by what the station sends and what the console reads back. */
const wakeReadinessFields = {
    /** When the station checked, RFC 3339 UTC. */
    checkedAt: IsoDateTimeSchema,
    /** Fast Startup is off (`HiberbootEnabled` = 0), so "shut down" is a real S5 the NIC can wake from. */
    fastStartupOff: z.boolean(),
    /** The physical wired adapters; Wi-Fi and virtual adapters are left out. */
    adapters: z.array(WakeAdapterSchema).max(MAX_WAKE_ADAPTERS),
    /** Fast Startup off and at least one adapter ready: the PC will wake, the BIOS permitting. */
    ready: z.boolean(),
    /** Why it is not ready, most important first; empty when it is. */
    issues: z.array(WakeIssueSchema).max(WAKE_ISSUES.length),
};
export const StationWakeReadinessReportSchema = z
    .object(wakeReadinessFields)
    .openapi('StationWakeReadinessReport', {
    description: 'Whether a station is set up to be woken by Wake-on-LAN, sent when it changes (F-STN-17, spec/10 §6.5).',
});
export const putStationWakeReadinessRoute = createRoute({
    method: 'put',
    path: '/v1/station/wake-readiness',
    operationId: 'putStationWakeReadiness',
    tags: ['stations'],
    summary: 'The station reports whether it can be woken by Wake-on-LAN',
    description: `${STATION_AUTHENTICATION} Replaces the server's record of the calling station's Wake-on-LAN readiness — Fast ` +
        'Startup, and each wired adapter with its MAC and whether it is armed (F-STN-17, spec/10 §6.5). Sent at startup, ' +
        'after pairing and when the result changes; never as a heartbeat. The armed adapters’ MACs are what a wake uses.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationWakeReadinessReportSchema } }, required: true } },
    responses: {
        204: { description: 'The report was stored.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{o}/venues/{v}/stations/{stationId}/wake-readiness
// ---------------------------------------------------------------------------------------------
export const StationWakeParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationWakeParams');
/** What the station last reported, stamped with when the server received it. */
export const StationWakeReadinessSchema = z
    .object({
    ...wakeReadinessFields,
    /** When the server stored the report. */
    reportedAt: IsoDateTimeSchema,
})
    .openapi('StationWakeReadiness', { description: 'A station’s last Wake-on-LAN readiness report (F-STN-17).' });
export const StationWakeStatusSchema = z
    .object({
    /** The station's last report, or null when it has never sent one (a station older than this report). */
    readiness: StationWakeReadinessSchema.nullable(),
    /** The MAC addresses a wake sends the magic packet to: the armed adapters', then the pairing's. Empty: it cannot be woken. */
    macAddresses: z.array(WakeMacAddressSchema),
})
    .openapi('StationWakeStatus', { description: 'Whether a station will wake from Wake-on-LAN, and the addresses a wake uses (F-STN-17, spec/13 §3).' });
export const getStationWakeStatusRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/wake-readiness',
    operationId: 'getStationWakeStatus',
    tags: ['stations'],
    summary: 'Whether a station will wake from Wake-on-LAN',
    description: 'The station’s last Wake-on-LAN readiness report (Fast Startup, each wired adapter and whether it is armed) and the ' +
        'MAC addresses a wake uses — for the console’s station drawer ("Wake-on-LAN: ready" / "needs attention") ' +
        '(F-STN-17, spec/13 §3). Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: StationWakeParamsSchema },
    responses: {
        200: jsonResponse(StationWakeStatusSchema, 'The station’s Wake-on-LAN readiness.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such station at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/wake-list — the venue's wake list, cached on every device (2026-10-01)
// ---------------------------------------------------------------------------------------------
/** The most devices one wake list carries — a venue's whole floor and its counters. */
export const MAX_WAKE_LIST_STATIONS = 1000;
/** At most this many subnet broadcast addresses per device. */
export const MAX_WAKE_BROADCASTS = 8;
/**
 * `station.idlePowerAction` (F-STN-10, spec/10 §6.5): what a station does after `station.idlePowerMinutes` locked on
 * the attract screen with no one at it. `none` — stay on — is the default and what every station does today.
 */
export const STATION_IDLE_POWER_ACTIONS = ['none', 'sleep', 'shutdown'];
export const StationIdlePowerActionSchema = z.enum(STATION_IDLE_POWER_ACTIONS).openapi('StationIdlePowerAction', {
    description: 'What a locked, idle station does after `station.idlePowerMinutes`: nothing (`none`), sleep, or shut down.',
});
/** `station.idlePowerMinutes`: the bounds and the spec's default (30). */
export const STATION_IDLE_POWER_MINUTES_MIN = 5;
export const STATION_IDLE_POWER_MINUTES_MAX = 240;
export const STATION_IDLE_POWER_MINUTES_DEFAULT = 30;
/** One device of the venue, as a wake reaches it with no server. */
export const WakeListStationSchema = z
    .object({
    stationId: StationIdSchema,
    /** The number on the tile. */
    number: z.int().min(1),
    /** The station's optional label; null when it goes by its number alone. */
    name: z.string().nullable(),
    /** `station` or `counter` (Clavian Counter). */
    role: StationRoleSchema,
    /** The MACs a wake sends to: the armed adapters', then its other wired adapters', then the pairing's. Empty: it cannot be woken. */
    macAddresses: z.array(WakeMacAddressSchema).max(MAX_WAKE_ADAPTERS),
    /** The directed broadcast addresses of the subnets it reported (`192.168.1.255`), in addition to 255.255.255.255. */
    broadcastAddresses: z.array(Ipv4AddressSchema).max(MAX_WAKE_BROADCASTS),
    /** The station's own readiness verdict (Fast Startup off and an armed adapter); null before its first report. */
    wakeReady: z.boolean().nullable(),
    /** The last mode the server knew, when the list was made (`locked`, `in_session`, …). */
    mode: StationModeSchema,
    /** Whether it was connected to the cloud when the list was made. */
    online: z.boolean(),
})
    .openapi('WakeListStation', { description: 'A device of the venue as a local Wake-on-LAN reaches it (F-STN-17, spec/10 §6.5).' });
export const StationWakeListSchema = z
    .object({
    venueId: VenueIdSchema,
    /** The venue's IANA timezone: opening hours are local times in it. */
    timezone: TimezoneSchema,
    openingHours: OpeningHoursSchema,
    holidays: HolidaysSchema,
    /**
     * `venue.powerSchedule` is `opening_hours`: wake every PC `wakeLeadMinutes` before each opening. False when it is
     * `off` (the default). Flattened from the setting's union so every generated client reads it alike.
     */
    wakeBeforeOpening: z.boolean(),
    /** Minutes before opening to wake (`venue.powerSchedule.wakeLeadMinutes`); 0 when `wakeBeforeOpening` is false. */
    wakeLeadMinutes: z.int().min(0).max(120),
    /**
     * `venue.powerSchedule` is `opening_hours`: shut down each locked station with no session `shutdownGraceMinutes`
     * after each closing, and keep retrying one in a session until it is free — never during a session (F-STN-10).
     * False when the schedule is `off` (the default).
     */
    shutdownAfterClosing: z.boolean(),
    /** Minutes after closing before the shutdown (`venue.powerSchedule.shutdownGraceMinutes`); 0 when off. */
    shutdownGraceMinutes: z.int().min(0).max(240),
    /** `station.idlePowerAction` resolved for the calling station: `none` (the default), `sleep` or `shutdown`. */
    idlePowerAction: StationIdlePowerActionSchema,
    /** `station.idlePowerMinutes` resolved for the calling station: how long locked and idle before the action. */
    idlePowerMinutes: z.int().min(STATION_IDLE_POWER_MINUTES_MIN).max(STATION_IDLE_POWER_MINUTES_MAX),
    /** Every paired device of the venue, the caller included, by number. */
    stations: z.array(WakeListStationSchema).max(MAX_WAKE_LIST_STATIONS),
    /** When the server made the list, RFC 3339 UTC. */
    generatedAt: IsoDateTimeSchema,
})
    .openapi('StationWakeList', {
    description: 'The venue’s wake list — every device’s MACs and subnet broadcasts, the opening hours and the power schedule — which each ' +
        'station and Clavian Counter keep so they can wake the venue’s PCs over the LAN with no cloud (F-STN-17, spec/10 §6.5).',
});
export const getStationWakeListRoute = createRoute({
    method: 'get',
    path: '/v1/station/wake-list',
    operationId: 'getStationWakeList',
    tags: ['stations'],
    summary: 'The venue’s wake list, for waking PCs with no cloud',
    description: `${STATION_AUTHENTICATION} Every paired device of the calling station’s venue with the MACs and subnet broadcast ` +
        'addresses a Wake-on-LAN uses, the venue’s opening hours, holidays and timezone, and `venue.powerSchedule`. The ' +
        'station and Clavian Counter read it on every connect and every few minutes and keep the last copy, so Wake all, ' +
        'wake one and the scheduled opening-hours wake work over the LAN when the cloud is unreachable (F-STN-17, spec/10 §6.5).',
    responses: {
        200: jsonResponse(StationWakeListSchema, 'The wake list.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/wake-sends — what a device woke on its own, reported when next online
// ---------------------------------------------------------------------------------------------
/**
 * What started a local wake: Clavian Counter's **Wake all** or **wake one**, the scheduled opening-hours wake, or the
 * kiosk staff menu's **Wake all** (a staff code).
 */
export const WAKE_SEND_TRIGGERS = ['counter_all', 'counter_station', 'schedule', 'kiosk_staff'];
export const WakeSendTriggerSchema = z.enum(WAKE_SEND_TRIGGERS).openapi('WakeSendTrigger', {
    description: 'What started a local wake: Clavian Counter’s Wake all (`counter_all`) or wake one (`counter_station`), the scheduled ' +
        'opening-hours wake (`schedule`), or the kiosk staff menu (`kiosk_staff`).',
});
export const StationWakeSendReportSchema = z
    .object({
    /** The device's own id for the send, so a retried report is recorded once. */
    localId: z.string().trim().min(1).max(64),
    trigger: WakeSendTriggerSchema,
    /** When the packets went out, RFC 3339 UTC (by the device's clock). */
    sentAt: IsoDateTimeSchema,
    /** The devices the packets were for. */
    stationIds: z.array(StationIdSchema).max(MAX_WAKE_LIST_STATIONS),
    /** How many UDP datagrams went out (MACs × addresses × ports × repeats). */
    packets: z.int().min(0).max(1_000_000),
})
    .openapi('StationWakeSendReport', { description: 'A wake a device sent on its own over the LAN, reported when it is next online (F-STN-17).' });
export const postStationWakeSendRoute = createRoute({
    method: 'post',
    path: '/v1/station/wake-sends',
    operationId: 'postStationWakeSend',
    tags: ['stations'],
    summary: 'The device reports a wake it sent on its own',
    description: `${STATION_AUTHENTICATION} Records a Wake-on-LAN the calling device sent over the LAN without the server — Clavian ` +
        'Counter’s Wake all or wake one, the scheduled opening-hours wake, or the kiosk staff menu — as an audit row ' +
        '(`stations.wake.sent`). Sent when the device is next online; a retry with the same `localId` is recorded once.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationWakeSendReportSchema } }, required: true } },
    responses: {
        204: { description: 'The send was recorded (or already had been).' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-wake.js.map